import * as Sentry from "@sentry/react";
import { Component, ReactNode, ErrorInfo } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  eventId: string | null;
}

/**
 * Error Boundary component with Sentry integration.
 *
 * Catches JavaScript errors in child components and:
 * 1. Reports them to Sentry (if configured)
 * 2. Displays a user-friendly error message
 * 3. Provides recovery options (retry, report)
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      eventId: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Report to Sentry
    Sentry.withScope((scope) => {
      scope.setExtras({
        componentStack: errorInfo.componentStack,
      });
      const eventId = Sentry.captureException(error);
      this.setState({ eventId });
    });

    // Log locally for development
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null, eventId: null });
  };

  handleReport = (): void => {
    if (this.state.eventId) {
      Sentry.showReportDialog({ eventId: this.state.eventId });
    }
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Custom fallback provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <Card className="max-w-md w-full">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <AlertTriangle className="h-12 w-12 text-destructive" />
              </div>
              <CardTitle className="text-xl">Something went wrong</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground text-center">
                We apologize for the inconvenience. An error occurred while
                loading this page.
              </p>

              {/* Error details in development */}
              {import.meta.env.DEV && this.state.error && (
                <div className="bg-muted p-3 rounded text-sm font-mono overflow-auto max-h-32">
                  {this.state.error.message}
                </div>
              )}

              <div className="flex flex-col gap-2">
                <Button onClick={this.handleReset} className="w-full">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Try Again
                </Button>

                {this.state.eventId && (
                  <Button
                    variant="outline"
                    onClick={this.handleReport}
                    className="w-full"
                  >
                    Report Issue
                  </Button>
                )}

                <Button
                  variant="ghost"
                  onClick={() => window.location.reload()}
                  className="w-full"
                >
                  Reload Page
                </Button>
              </div>

              {this.state.eventId && (
                <p className="text-xs text-muted-foreground text-center">
                  Error ID: {this.state.eventId}
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Sentry-wrapped Error Boundary.
 * Use this for automatic Sentry integration.
 */
export const SentryErrorBoundary = Sentry.withErrorBoundary(ErrorBoundary, {
  showDialog: false, // We handle dialog manually
});

export default ErrorBoundary;
